import type { Command } from '../../types/commands'
import { formatCommandOutput, colorize, formatHighlight } from '../colorScheme'
import { storyOverview, storyChapters } from '../data/storyContent'
import { dividers } from '../asciiArt'

function buildOverview(): string {
  const chapterList = storyChapters
    .map((ch) => `  ${colorize('▸', 'cyan')} ${formatHighlight(`story ${ch.key}`)}  ${colorize(ch.title, 'brightBlack')}`)
    .join('\n')

  return `
╔═══════════════════════════════════════════════════════════╗
║              ${formatHighlight('FROM CLAY TO CODE')}                              ║
║              ${colorize('A Developer Story', 'brightBlack')}                              ║
╚═══════════════════════════════════════════════════════════╝

${storyOverview}

${colorize(dividers.circuit, 'brightBlack')}

╭─ 📖 CHAPTERS ────────────────────────────────────────────╮
│                                                            │
${chapterList}
│                                                            │
╰────────────────────────────────────────────────────────────╯

~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~
  >> Type ${formatHighlight('story <section>')} to read a chapter
  >> Example: ${formatHighlight('story clay')} or ${formatHighlight('story taxjar')}
~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~
`
}

function buildChapter(key: string): string | null {
  const chapter = storyChapters.find(
    (ch) => ch.key === key || ch.keywords.includes(key.toLowerCase()),
  )
  if (!chapter) return null

  return `
╔═══════════════════════════════════════════════════════════╗
║  ${formatHighlight(chapter.title.toUpperCase().padEnd(55))}║
╚═══════════════════════════════════════════════════════════╝

${chapter.content}

${colorize(dividers.stars, 'yellow')}

  >> Type ${formatHighlight('story')} to see all chapters
`
}

export const storyCommand: Command = {
  name: 'story',
  description: 'Read the full developer story',
  aliases: ['mystory', 'journey', 'origin'],
  usage: 'story [section]',
  category: 'info',
  execute: (args) => {
    const section = args[0]?.toLowerCase()
    const text = section ? buildChapter(section) : buildOverview()

    if (!text) {
      const available = storyChapters.map((ch) => ch.key).join(', ')
      return [
        {
          type: 'error',
          content: `Unknown section: "${section}". Available: ${available}`,
        },
      ]
    }

    const formattedText = formatCommandOutput(text, {
      highlightTech: true,
      highlightNumbers: true,
      highlightDates: false,
      highlightPaths: false,
      highlightUrls: false,
    })

    return [
      {
        type: 'info',
        content: (
          <div dangerouslySetInnerHTML={{ __html: formattedText }} />
        ),
      },
    ]
  },
}
